// src/modules/CleaningChecklist.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircle, Clock, Users, Home, FileText, 
  Signature, ClipboardList, Shield, Droplet, Bed,
  Play, Square, Timer
} from 'lucide-react';

// ─── HARDCODED ROOMS (Roop Cottage – 41 areas) ──────────────
const HARDCODED_ROOMS = [
  { id: 1, room_number: 'Room 101', floor: 'Ground' },
  { id: 2, room_number: 'Room 102', floor: 'Ground' },
  { id: 3, room_number: 'Room 103', floor: 'Ground' },
  { id: 4, room_number: 'Room 104', floor: 'Ground' },
  { id: 5, room_number: 'Room 105', floor: 'Ground' },
  { id: 6, room_number: 'Room 106', floor: 'Ground' },
  { id: 7, room_number: 'Room 107', floor: 'Ground' },
  { id: 8, room_number: 'Room 108', floor: 'Ground' },
  { id: 9, room_number: 'Room 109', floor: 'Ground' },
  { id: 10, room_number: 'Room 110', floor: 'Ground' },
  { id: 11, room_number: 'Room 111', floor: 'Ground' },
  { id: 12, room_number: 'Room 112', floor: 'Ground' },
  { id: 13, room_number: 'Room 113', floor: 'Ground' },
  { id: 14, room_number: 'Room 114', floor: 'Ground' },
  { id: 15, room_number: 'Room 115', floor: 'Ground' },
  { id: 16, room_number: 'Room 116', floor: 'Ground' },
  { id: 17, room_number: 'Room 201', floor: 'First' },
  { id: 18, room_number: 'Room 202', floor: 'First' },
  { id: 19, room_number: 'Room 203', floor: 'First' },
  { id: 20, room_number: 'Room 204', floor: 'First' },
  { id: 21, room_number: 'Room 205', floor: 'First' },
  { id: 22, room_number: 'Room 206', floor: 'First' },
  { id: 23, room_number: 'Room 207', floor: 'First' },
  { id: 24, room_number: 'Room 208', floor: 'First' },
  { id: 25, room_number: 'Room 209', floor: 'First' },
  { id: 26, room_number: 'Room 210', floor: 'First' },
  { id: 27, room_number: 'Room 211', floor: 'First' },
  { id: 28, room_number: 'Room 212', floor: 'First' },
  { id: 29, room_number: 'Room 213', floor: 'First' },
  { id: 30, room_number: 'Room 214', floor: 'First' },
  { id: 31, room_number: 'Room 215', floor: 'First' },
  { id: 32, room_number: 'Room 216', floor: 'First' },
  { id: 33, room_number: 'Lounge (Ground)', floor: 'Ground' },
  { id: 34, room_number: 'Lounge (First)', floor: 'First' },
  { id: 35, room_number: 'Hallway (Ground)', floor: 'Ground' },
  { id: 36, room_number: 'Hallway (First)', floor: 'First' },
  { id: 37, room_number: 'Bathroom 1 (Ground)', floor: 'Ground' },
  { id: 38, room_number: 'Bathroom 2 (Ground)', floor: 'Ground' },
  { id: 39, room_number: 'Bathroom 1 (First)', floor: 'First' },
  { id: 40, room_number: 'Bathroom 2 (First)', floor: 'First' },
  { id: 41, room_number: 'Laundry Room', floor: 'Ground' },
];

// ─── HARDCODED CHECKLIST ITEMS (46+ tasks) ──────────────────
const HARDCODED_TASKS = [
  // Pre-Cleaning & Safety
  { id: 1, category: 'Pre-Cleaning & Safety', description: 'Hand hygiene performed – washed hands with soap and water or used alcohol gel' },
  { id: 2, category: 'Pre-Cleaning & Safety', description: 'PPE donned – clean disposable gloves and plastic apron' },
  { id: 3, category: 'Pre-Cleaning & Safety', description: 'Wet floor / Cleaning in Progress sign placed outside door' },
  { id: 4, category: 'Pre-Cleaning & Safety', description: 'Resident checked – confirmed out of room or safely positioned, task explained' },
  { id: 5, category: 'Pre-Cleaning & Safety', description: 'Cross-contamination setup – blue/green cloths for bedroom, red cloths/mops for en-suite' },
  // Waste Management
  { id: 6, category: 'Waste Management', description: 'Domestic waste (black bag) emptied into appropriate waste trolley' },
  { id: 7, category: 'Waste Management', description: 'Clinical waste (yellow/tiger bag) segregated and emptied correctly' },
  { id: 8, category: 'Waste Management', description: 'Waste bins sanitized – inside and outside wiped with disinfectant' },
  { id: 9, category: 'Waste Management', description: 'Fresh, correctly color-coded bin liners fitted' },
  { id: 10, category: 'Waste Management', description: 'Soiled linen placed directly into alginate/water-soluble bags (never on floor/furniture)' },
  // Bedroom Cleaning
  { id: 11, category: 'Bedroom Cleaning', description: 'High-level dusting – tops of wardrobes, doors, picture frames, curtain rails' },
  { id: 12, category: 'Bedroom Cleaning', description: 'Light fixtures & vents – dusted light fittings, switches, ventilation grilles' },
  { id: 13, category: 'Bedroom Cleaning', description: 'Window areas – internal window glass, windowsills, window restrictors/latches cleaned' },
  { id: 14, category: 'Bedroom Cleaning', description: 'Walls & radiators – spot-cleaned marks on walls, damp-wiped radiator casings and pipework' },
  { id: 15, category: 'Bedroom Cleaning', description: 'Furniture surfaces – bedside tables, over-bed tables, chests of drawers, vanity units wiped' },
  { id: 16, category: 'Bedroom Cleaning', description: 'Seating – all chairs (armrests, seats, legs) wiped clean, pressure cushions checked' },
  { id: 17, category: 'Bedroom Cleaning', description: 'High-touch disinfection – light switches, door handles, push plates, wardrobe handles (1,000 ppm chlorine)' },
  // Medical Bed & Equipment
  { id: 18, category: 'Medical Bed & Equipment', description: 'Nurse call system – call button, cord, wall bracket disinfected' },
  { id: 19, category: 'Medical Bed & Equipment', description: 'Bed rails & controls – both sides of rails, under-rail mechanisms, hand control pendant wiped' },
  { id: 20, category: 'Medical Bed & Equipment', description: 'Mattress inspection – covers unzipped, internal foam checked for fluid ingress/staining' },
  { id: 21, category: 'Medical Bed & Equipment', description: 'Mattress disinfection – external covers wiped with disinfectant' },
  { id: 22, category: 'Medical Bed & Equipment', description: 'Bed frame – headboard, footboard, under-bed framework/casters damp-wiped' },
  // En-Suite Bathroom
  { id: 23, category: 'En-Suite Bathroom', description: 'Mirrors & ledges – cleaned mirrors, splashbacks, high ledges' },
  { id: 24, category: 'En-Suite Bathroom', description: 'Handwash basin – taps, basin surfaces, plug holes cleaned and descaled' },
  { id: 25, category: 'En-Suite Bathroom', description: 'Shower/Bath area – tiles, shower heads, fixtures, shower chairs cleaned' },
  { id: 26, category: 'En-Suite Bathroom', description: 'Grab rails – all assistance rails and pull cords disinfected' },
  { id: 27, category: 'En-Suite Bathroom', description: 'Toilet decontamination – flush handle, seat (both sides), lid, exterior ceramic base wiped' },
  { id: 28, category: 'En-Suite Bathroom', description: 'Toilet bowl – toilet cleaner applied, scrubbed with toilet brush' },
  { id: 29, category: 'En-Suite Bathroom', description: 'Dispenser replenishment – soap, paper towels, toilet rolls restocked, dispensers wiped' },
  // Floor Care
  { id: 30, category: 'Floor Care', description: 'Debris removal – carpets vacuumed with HEPA-filter vacuum' },
  { id: 31, category: 'Floor Care', description: 'Hard floors – damp-mopped using two-bucket system from furthest corner toward door' },
  { id: 32, category: 'Floor Care', description: 'Colour-coded equipment – blue/green mop for bedroom, red mop for bathroom' },
  { id: 33, category: 'Floor Care', description: 'Floor drying – wet floor sign remains until flooring is completely dry' },
  // COSHH Safety
  { id: 34, category: 'COSHH Safety', description: 'Chemical dwell time – manufacturer contact time achieved for virucidal action' },
  { id: 35, category: 'COSHH Safety', description: 'Product dilution – diluted via automated system or exact tablet-to-water ratio' },
  { id: 36, category: 'COSHH Safety', description: 'COSHH storage – housekeeping trolley or chemical cupboard locked immediately after use' },
  { id: 37, category: 'COSHH Safety', description: 'PPE compliance – correct PPE worn for chemicals used (gloves, goggles if required)' },
  // Weekly
  { id: 38, category: 'Weekly Deep Disinfection', description: 'High-touch surfaces – door frames, push plates, light switches wiped with 1,000ppm chlorine' },
  { id: 39, category: 'Weekly Deep Disinfection', description: 'Mattress deep clean – unzip covers, inspect internal foam for fluid ingress/staining' },
  { id: 40, category: 'Weekly Deep Disinfection', description: 'Window & ledges – internal window glass, windowsills, curtain rails vacuumed' },
  { id: 41, category: 'Weekly Deep Disinfection', description: 'Radiators & vents – radiator casings, pipework, ventilation grilles damp-wiped' },
  { id: 42, category: 'Weekly Deep Disinfection', description: 'Chair & upholstery – all seating vacuumed and spot-cleaned including pressure cushions' },
  { id: 43, category: 'Weekly Deep Disinfection', description: 'Skirting boards – all skirting boards dusted and spot-cleaned' },
  { id: 44, category: 'Weekly Deep Disinfection', description: 'Behind furniture – beds and furniture moved, area behind cleaned' },
  // Monthly
  { id: 45, category: 'Monthly Deep Clean', description: 'En-suite deep clean – grout cleaned, shower heads descaled, toilet bases deep scrubbed' },
  { id: 46, category: 'Monthly Deep Clean', description: 'Furniture rotation – beds and furniture moved, behind/underneath cleaned' },
  { id: 47, category: 'Monthly Deep Clean', description: 'Curtains & blinds – fully laundered or vacuumed completely' },
  { id: 48, category: 'Monthly Deep Clean', description: 'Emergency equipment – pull cords, nurse call functionality, oxygen equipment checked for dust' },
  { id: 49, category: 'Monthly Deep Clean', description: 'COSHH stock audit – chemical expiry dates checked, spill kits replenished, Safety Data Sheets updated' },
  { id: 50, category: 'Monthly Deep Clean', description: 'Vents & grilles – all ventilation grilles deep-cleaned' },
  { id: 51, category: 'Monthly Deep Clean', description: 'Light fixtures – all light fixtures deep-cleaned and bulbs checked' },
];

interface ItemLog {
  id: number;
  template_id: number;
  status: 'Pending' | 'Pass' | 'Fail' | 'N/A';
  time_taken_minutes: number;
  notes: string;
  reason_for_failure: string;
  maintenance_ref: string;
  category: string;
  task_description: string;
  // Timer state
  timerRunning: boolean;
  elapsedSeconds: number;
}

interface Submission {
  id: number;
  room_id: number;
  frequency: string;
  scheme: string;
  status: string;
  start_time: string;
  end_time: string | null;
  total_minutes: number | null;
  staff_name: string;
  staff_signed_at: string | null;
  supervisor_name: string | null;
  supervisor_signed_at: string | null;
  manager_name: string | null;
  manager_signed_at: string | null;
  items: ItemLog[];
}

export function CleaningChecklist({ user }: { user: any }) {
  const [selectedRoom, setSelectedRoom] = useState<number>(0);
  const [frequency, setFrequency] = useState<'Daily' | 'Weekly' | 'Monthly'>('Daily');
  const [scheme, setScheme] = useState('Routine Daily Clean');
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [view, setView] = useState<'checklist' | 'history'>('checklist');
  const [password, setPassword] = useState('');
  const [signingType, setSigningType] = useState<'staff' | 'supervisor' | 'manager' | null>(null);
  const [error, setError] = useState('');
  
  // Timer refs
  const timerIntervals = useRef<{ [key: number]: NodeJS.Timeout }>({});

  // ─── Start Checklist ──────────────────────────────────────
  const startChecklist = () => {
    if (!selectedRoom) return alert('Please select a room or area');
    setLoading(true);
    setError('');

    let tasks = HARDCODED_TASKS;
    if (frequency === 'Daily') {
      tasks = tasks.filter(t => !t.category.includes('Weekly') && !t.category.includes('Monthly'));
    } else if (frequency === 'Weekly') {
      tasks = tasks.filter(t => t.category.includes('Weekly') || !t.category.includes('Monthly'));
    } else if (frequency === 'Monthly') {
      tasks = tasks.filter(t => t.category.includes('Monthly') || t.category.includes('Weekly'));
    }

    const items: ItemLog[] = tasks.map((t, index) => ({
      id: Date.now() + index,
      template_id: t.id,
      status: 'Pending',
      time_taken_minutes: 0,
      notes: '',
      reason_for_failure: '',
      maintenance_ref: '',
      category: t.category,
      task_description: t.description,
      timerRunning: false,
      elapsedSeconds: 0,
    }));

    const newSubmission: Submission = {
      id: Date.now(),
      room_id: selectedRoom,
      frequency,
      scheme,
      status: 'In Progress',
      start_time: new Date().toISOString(),
      end_time: null,
      total_minutes: null,
      staff_name: user?.name || 'Unknown',
      staff_signed_at: null,
      supervisor_name: null,
      supervisor_signed_at: null,
      manager_name: null,
      manager_signed_at: null,
      items,
    };

    setSubmission(newSubmission);
    setLoading(false);
  };

  // ─── Timer Functions ──────────────────────────────────────
  const startTimer = (itemId: number) => {
    if (!submission) return;
    const item = submission.items.find(i => i.id === itemId);
    if (!item) return;
    
    // Stop any existing timer for this item
    if (timerIntervals.current[itemId]) {
      clearInterval(timerIntervals.current[itemId]);
    }

    const updatedItems = submission.items.map(i => {
      if (i.id === itemId) {
        return { ...i, timerRunning: true };
      }
      return i;
    });
    setSubmission({ ...submission, items: updatedItems });

    // Start interval
    timerIntervals.current[itemId] = setInterval(() => {
      setSubmission(prev => {
        if (!prev) return prev;
        const updated = prev.items.map(i => {
          if (i.id === itemId) {
            return { ...i, elapsedSeconds: i.elapsedSeconds + 1 };
          }
          return i;
        });
        return { ...prev, items: updated };
      });
    }, 1000);
  };

  const stopTimer = (itemId: number) => {
    if (timerIntervals.current[itemId]) {
      clearInterval(timerIntervals.current[itemId]);
      delete timerIntervals.current[itemId];
    }

    if (!submission) return;
    const item = submission.items.find(i => i.id === itemId);
    if (!item) return;

    // Convert elapsed seconds to minutes (rounded up)
    const minutes = Math.ceil(item.elapsedSeconds / 60);
    
    const updatedItems = submission.items.map(i => {
      if (i.id === itemId) {
        return { 
          ...i, 
          timerRunning: false,
          time_taken_minutes: minutes > 0 ? minutes : 1 // Minimum 1 minute
        };
      }
      return i;
    });
    setSubmission({ ...submission, items: updatedItems });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ─── Update Item Status ──────────────────────────────────
  const updateItemStatus = (logId: number, status: 'Pass' | 'Fail' | 'N/A', reason?: string, maintRef?: string) => {
    if (!submission) return;
    
    // If timer is running, stop it first
    if (timerIntervals.current[logId]) {
      stopTimer(logId);
    }

    const item = submission.items.find(i => i.id === logId);
    const timeTaken = item?.time_taken_minutes || 0;

    const updatedItems = submission.items.map(item => {
      if (item.id === logId) {
        return {
          ...item,
          status,
          reason_for_failure: reason || '',
          maintenance_ref: maintRef || '',
          timerRunning: false,
        };
      }
      return item;
    });
    setSubmission({ ...submission, items: updatedItems });
  };

  // ─── Sign Off ─────────────────────────────────────────────
  const handleSignOff = (type: 'staff' | 'supervisor' | 'manager') => {
    if (!password) return alert('Please enter your password to sign.');
    if (!submission) return;
    
    // Stop all timers
    Object.keys(timerIntervals.current).forEach(key => {
      clearInterval(timerIntervals.current[Number(key)]);
      delete timerIntervals.current[Number(key)];
    });

    // Check all items completed
    if (!submission.items.every(i => i.status !== 'Pending')) {
      return alert('Complete all tasks before signing off.');
    }
    
    const updatedSubmission = { ...submission };
    const now = new Date().toISOString();
    let totalMins = 0;

    if (type === 'manager') {
      totalMins = updatedSubmission.items.reduce((sum, i) => sum + i.time_taken_minutes, 0);
      updatedSubmission.total_minutes = totalMins;
      updatedSubmission.end_time = now;
      updatedSubmission.status = 'Completed';
      updatedSubmission.manager_name = user?.name || 'Manager';
      updatedSubmission.manager_signed_at = now;
    } else if (type === 'staff') {
      updatedSubmission.staff_signed_at = now;
      updatedSubmission.status = 'Pending Supervisor';
    } else if (type === 'supervisor') {
      updatedSubmission.supervisor_name = user?.name || 'Deputy';
      updatedSubmission.supervisor_signed_at = now;
      updatedSubmission.status = 'Pending Manager';
    }

    setSubmission(updatedSubmission);
    setPassword('');
    setSigningType(null);
    alert(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} sign-off recorded.`);

    if (type === 'manager') {
      const room = HARDCODED_ROOMS.find(r => r.id === submission.room_id);
      setHistory(prev => [{
        id: submission.id,
        room_number: room?.room_number || 'Unknown',
        floor: room?.floor || '',
        frequency: submission.frequency,
        staff_name: submission.staff_name,
        staff_signed_at: updatedSubmission.staff_signed_at,
        supervisor_name: updatedSubmission.supervisor_name,
        supervisor_signed_at: updatedSubmission.supervisor_signed_at,
        manager_name: updatedSubmission.manager_name,
        manager_signed_at: updatedSubmission.manager_signed_at,
        total_minutes: totalMins,
        status: 'Completed',
        submitted_at: now,
      }, ...prev]);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────
  const getRoomLabel = (id: number) => {
    const r = HARDCODED_ROOMS.find(r => r.id === id);
    return r ? `${r.room_number} (${r.floor})` : 'Unknown';
  };

  const renderStatusBadge = (status: string) => {
    const map: any = {
      'In Progress': <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">In Progress</span>,
      'Pending Supervisor': <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Pending Supervisor</span>,
      'Pending Manager': <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">Pending Manager</span>,
      'Completed': <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Completed</span>,
    };
    return map[status] || <span className="bg-gray-100 px-2 py-1 rounded text-xs">{status}</span>;
  };

  const groupedItems = submission?.items?.reduce((acc: any, item) => {
    const cat = item.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  const allItemsCompleted = () => {
    if (!submission?.items) return false;
    return submission.items.every(i => i.status !== 'Pending');
  };

  const isSigned = (type: 'staff' | 'supervisor' | 'manager') => {
    if (type === 'staff') return !!submission?.staff_signed_at;
    if (type === 'supervisor') return !!submission?.supervisor_signed_at;
    if (type === 'manager') return !!submission?.manager_signed_at;
  };

  const canSign = (type: 'staff' | 'supervisor' | 'manager') => {
    const role = user?.role;
    if (type === 'staff') return ['CareStaff', 'Staff', 'TeamLeader', 'SeniorCarer', 'Domestic', 'Maintenance'].includes(role) && !isSigned('staff');
    if (type === 'supervisor') return ['DeputyManager', 'TeamLeader', 'RegisteredManager'].includes(role) && !isSigned('supervisor') && isSigned('staff');
    if (type === 'manager') return ['RegisteredManager', 'SuperAdmin'].includes(role) && !isSigned('manager') && isSigned('supervisor');
    return false;
  };

  const getCompletedCount = () => {
    if (!submission?.items) return 0;
    return submission.items.filter(i => i.status !== 'Pending').length;
  };

  const getTotalTasks = () => {
    return submission?.items?.length || 0;
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.keys(timerIntervals.current).forEach(key => {
        clearInterval(timerIntervals.current[Number(key)]);
      });
    };
  }, []);

  // ─── Render ──────────────────────────────────────────────
  return (
    <div className="p-6 bg-[#F9F8F5] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
          <Home className="w-6 h-6" /> Room Cleaning Checklist
        </h1>
        <div className="flex gap-2">
          <button 
            onClick={() => setView('checklist')} 
            className={`px-4 py-2 rounded-lg ${view === 'checklist' ? 'bg-[#123B36] text-white' : 'bg-white text-gray-600'}`}
          >Checklist</button>
          <button 
            onClick={() => setView('history')} 
            className={`px-4 py-2 rounded-lg ${view === 'history' ? 'bg-[#123B36] text-white' : 'bg-white text-gray-600'}`}
          >History</button>
        </div>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">{error}</div>}

      {view === 'checklist' && (
        <>
          {!submission ? (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-[#E1DCCD] max-w-2xl">
              <h2 className="text-lg font-semibold mb-4">Start New Room / Area Cleaning</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Room / Area</label>
                  <select value={selectedRoom} onChange={(e) => setSelectedRoom(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2 mt-1">
                    <option value={0}>Select Room / Area</option>
                    {HARDCODED_ROOMS.map(r => (
                      <option key={r.id} value={r.id}>{r.room_number} ({r.floor})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Frequency</label>
                  <select value={frequency} onChange={(e) => setFrequency(e.target.value as any)} className="w-full border rounded-lg px-3 py-2 mt-1">
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Cleaning Scheme</label>
                  <select value={scheme} onChange={(e) => setScheme(e.target.value)} className="w-full border rounded-lg px-3 py-2 mt-1">
                    <option>Routine Daily Clean</option>
                    <option>Deep Clean / Turn-Over</option>
                    <option>Outbreak Protocol (1,000 ppm Chlorine)</option>
                  </select>
                </div>
              </div>
              <button onClick={startChecklist} disabled={loading} className="mt-4 bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28] w-full disabled:opacity-50">
                {loading ? 'Loading...' : 'Start / Resume Checklist'}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header Card */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-[#E1DCCD] flex flex-wrap justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">{getRoomLabel(submission.room_id)}</h2>
                  <p className="text-sm text-gray-500">{submission.frequency} • {submission.scheme}</p>
                  <div className="mt-1 flex items-center gap-2">
                    {renderStatusBadge(submission.status)}
                    <span className="text-xs text-gray-400">
                      {getCompletedCount()}/{getTotalTasks()} tasks completed
                    </span>
                  </div>
                </div>
                <div className="text-right text-sm">
                  <p><span className="font-semibold">Staff:</span> {submission.staff_name} {submission.staff_signed_at && '✅'}</p>
                  <p><span className="font-semibold">Deputy:</span> {submission.supervisor_name || 'Pending'} {submission.supervisor_signed_at && '✅'}</p>
                  <p><span className="font-semibold">Manager:</span> {submission.manager_name || 'Pending'} {submission.manager_signed_at && '✅'}</p>
                </div>
                <button onClick={() => setSubmission(null)} className="text-sm text-[#123B36] underline">New Room</button>
              </div>

              {/* Checklist Items */}
              {Object.keys(groupedItems).map((category) => (
                <div key={category} className="bg-white p-4 rounded-xl shadow-sm border border-[#E1DCCD]">
                  <h3 className="font-bold text-[#123B36] border-b pb-2 mb-3 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" /> {category}
                  </h3>
                  <div className="space-y-3">
                    {groupedItems[category].map((item: ItemLog) => (
                      <div key={item.id} className="flex items-start gap-4 p-2 hover:bg-[#F1EFE9] rounded-lg transition border-b border-gray-50">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.task_description}</p>
                          {item.status === 'Fail' && (
                            <div className="mt-1 text-xs text-red-600 bg-red-50 p-1 rounded">
                              <strong>Failure Reason:</strong> {item.reason_for_failure || 'Not specified'}
                              {item.maintenance_ref && <span className="ml-2">• Maintenance Ref: {item.maintenance_ref}</span>}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {item.status === 'Pending' ? (
                              <div className="flex items-center gap-1">
                                {item.timerRunning ? (
                                  <>
                                    <span className="text-sm font-mono text-green-600">
                                      ⏱ {formatTime(item.elapsedSeconds)}
                                    </span>
                                    <button 
                                      onClick={() => stopTimer(item.id)}
                                      className="px-2 py-0.5 rounded text-xs bg-red-600 text-white hover:bg-red-700"
                                    >
                                      <Square className="w-3 h-3 inline" /> Stop
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-xs text-gray-400">
                                      {item.time_taken_minutes > 0 ? `${item.time_taken_minutes} min` : '⏱ Not started'}
                                    </span>
                                    <button 
                                      onClick={() => startTimer(item.id)}
                                      className="px-2 py-0.5 rounded text-xs bg-[#123B36] text-white hover:bg-[#0E2C28]"
                                    >
                                      <Play className="w-3 h-3 inline" /> Start Timer
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">
                                ⏱ {item.time_taken_minutes} min spent
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.status === 'Pending' ? (
                            <div className="flex items-center gap-1">
                              <button 
                                onClick={() => updateItemStatus(item.id, 'Pass')}
                                className="px-2 py-1 rounded text-xs bg-green-600 text-white hover:bg-green-700"
                              >Pass</button>
                              <button 
                                onClick={() => {
                                  const reason = prompt('Why did this task fail? (Please provide detailed reason for CQC compliance)');
                                  if (reason) {
                                    const maintRef = prompt('Maintenance reference number (if applicable):');
                                    updateItemStatus(item.id, 'Fail', reason, maintRef || '');
                                  }
                                }} 
                                className="px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700"
                              >Fail</button>
                              <button 
                                onClick={() => updateItemStatus(item.id, 'N/A')}
                                className="px-2 py-1 rounded text-xs bg-gray-400 text-white hover:bg-gray-500"
                              >N/A</button>
                            </div>
                          ) : (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              item.status === 'Pass' ? 'bg-green-100 text-green-700' : 
                              item.status === 'Fail' ? 'bg-red-100 text-red-700' : 
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {item.status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Sign-off Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#E1DCCD]">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Signature className="w-5 h-5" /> Digital Sign-Off
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  All tasks must be marked as Pass, Fail, or N/A before signing. 
                  You must enter your password to confirm.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Staff Sign */}
                  <div className="border p-4 rounded-lg">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4" /> Housekeeper
                      {isSigned('staff') ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-yellow-500" />}
                    </h4>
                    {isSigned('staff') ? (
                      <p className="text-sm text-green-600">Signed by {submission.staff_name} at {new Date(submission.staff_signed_at!).toLocaleTimeString()}</p>
                    ) : (
                      canSign('staff') && (
                        <div className="mt-2">
                          <input 
                            type="password" 
                            placeholder="Enter your password" 
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={signingType === 'staff' ? password : ''}
                            onChange={(e) => { setSigningType('staff'); setPassword(e.target.value); }}
                          />
                          <button 
                            onClick={() => handleSignOff('staff')} 
                            disabled={!allItemsCompleted() || !password} 
                            className="mt-2 bg-[#123B36] text-white px-4 py-1 rounded text-sm w-full disabled:opacity-50"
                          >
                            {!allItemsCompleted() ? `${getCompletedCount()}/${getTotalTasks()} tasks remaining` : 'Sign as Housekeeper'}
                          </button>
                        </div>
                      )
                    )}
                  </div>

                  {/* Deputy Sign */}
                  <div className="border p-4 rounded-lg">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Deputy Manager
                      {isSigned('supervisor') ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-yellow-500" />}
                    </h4>
                    {isSigned('supervisor') ? (
                      <p className="text-sm text-green-600">Signed by {submission.supervisor_name} at {new Date(submission.supervisor_signed_at!).toLocaleTimeString()}</p>
                    ) : (
                      canSign('supervisor') && (
                        <div className="mt-2">
                          <input 
                            type="password" 
                            placeholder="Enter your password" 
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={signingType === 'supervisor' ? password : ''}
                            onChange={(e) => { setSigningType('supervisor'); setPassword(e.target.value); }}
                          />
                          <button 
                            onClick={() => handleSignOff('supervisor')} 
                            disabled={!allItemsCompleted() || !password} 
                            className="mt-2 bg-blue-600 text-white px-4 py-1 rounded text-sm w-full disabled:opacity-50"
                          >
                            {!allItemsCompleted() ? `${getCompletedCount()}/${getTotalTasks()} tasks remaining` : 'Verify as Deputy'}
                          </button>
                        </div>
                      )
                    )}
                  </div>

                  {/* Manager Sign */}
                  <div className="border p-4 rounded-lg">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Bed className="w-4 h-4" /> Home Manager
                      {isSigned('manager') ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Clock className="w-4 h-4 text-yellow-500" />}
                    </h4>
                    {isSigned('manager') ? (
                      <p className="text-sm text-green-600">Signed by {submission.manager_name} at {new Date(submission.manager_signed_at!).toLocaleTimeString()}</p>
                    ) : (
                      canSign('manager') && (
                        <div className="mt-2">
                          <input 
                            type="password" 
                            placeholder="Enter your password" 
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={signingType === 'manager' ? password : ''}
                            onChange={(e) => { setSigningType('manager'); setPassword(e.target.value); }}
                          />
                          <button 
                            onClick={() => handleSignOff('manager')} 
                            disabled={!allItemsCompleted() || !password} 
                            className="mt-2 bg-purple-600 text-white px-4 py-1 rounded text-sm w-full disabled:opacity-50"
                          >
                            {!allItemsCompleted() ? `${getCompletedCount()}/${getTotalTasks()} tasks remaining` : 'Approve as Manager'}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {isSigned('staff') && !isSigned('supervisor') && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    <strong>Next Step:</strong> Room cleaning completed by {submission.staff_name}. 
                    Waiting for Deputy Manager verification.
                  </div>
                )}
                {isSigned('supervisor') && !isSigned('manager') && (
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                    <strong>Next Step:</strong> Deputy Manager {submission.supervisor_name} has verified. 
                    Waiting for Home Manager final approval.
                  </div>
                )}
                {isSigned('manager') && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    <strong>✅ Completed:</strong> Room cleaning fully signed off by all parties.
                    Total time: {submission.total_minutes || 'N/A'} minutes.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* History View */}
      {view === 'history' && (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-[#E1DCCD] overflow-x-auto">
          {history.length === 0 ? (
            <p className="text-gray-500">No cleaning history yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F1EFE9] text-left">
                <tr>
                  <th className="p-2">Room / Area</th>
                  <th className="p-2">Frequency</th>
                  <th className="p-2">Staff</th>
                  <th className="p-2">Deputy</th>
                  <th className="p-2">Manager</th>
                  <th className="p-2">Total Time</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{h.room_number}</td>
                    <td className="p-2">{h.frequency}</td>
                    <td className="p-2">{h.staff_name} {h.staff_signed_at && '✅'}</td>
                    <td className="p-2">{h.supervisor_name || '-'} {h.supervisor_signed_at && '✅'}</td>
                    <td className="p-2">{h.manager_name || '-'} {h.manager_signed_at && '✅'}</td>
                    <td className="p-2">{h.total_minutes || 0} min</td>
                    <td className="p-2">{renderStatusBadge(h.status)}</td>
                    <td className="p-2">{new Date(h.submitted_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}